// Forms-turn module (openspec change `conversational-forms-turn-module`).
//
// Plain TypeScript, no React. A factory returns one instance per provider
// mount; there is no module-level mutable state, so every `FormsProvider`
// mount (and every test render) gets its own queue and live-turn set.
//
// The instance holds three things (design D2):
//   - the transport core, `runTransport`: the request half every forms turn
//     shares (slow timer, the race against the turn timeout, the minimum
//     thinking beat, classification). It is NEVER queued: field-help turns
//     call it directly and may overlap a main turn.
//   - the FIFO turn queue, with the live-turn set. Queuing any main turn ends
//     liveness for every turn that had already timed out; a turn joins the set
//     only at the moment it times out; its late reply consumes the id.
//   - the main-turn reply applier, parameterised by `kind` (visitor or
//     activation) and `delivery` (fresh or late).
//
// Callers see four operations (design D3): `sendVisitorTurn`,
// `retryVisitorTurn`, `runActivationTurn` (all queued) and `runTransport`.
//
// Time and `ask` reach this file only through the injected dependencies, so
// the host can hand in call-time wrappers and Jest's fake timers keep
// governing them.
//
// Two moments for reading render values (design D1 item 11, D4):
//   - a visitor turn copies `depsRef.current` synchronously when the entry
//     point is called, and its run and late handler use that copy;
//   - an activation turn copies it when the queue starts it.
// Forms state is always read live, through the copy's `getFormsState`, when
// the turn leaves the queue.

import type api from '../../API/api';
import type { Answer, FormFill } from '../../API/types';
import type { ChatMessage, FillReceipt } from '../Shared/API/ApiTypes';
import { TURN_ERROR_MESSAGE, TURN_TIMEOUT_MESSAGE } from './constants';
import type { CfRegistry } from './formsConfig';
import type { CfAction, CfState } from './formsReducer';
import { openForAI } from './formsSelectors';
import type { CfForm } from './types';
import { classifyTurn, fillsRetryable, toOpenFieldSpecs, TurnOutcome } from './turnHelpers';

// Normative timings (moved here from `constants.ts`, design D6). Animation
// collapses under `prefers-reduced-motion: reduce`; the latency thresholds do
// not — they are behavior, not motion.

/** Typing bubble gains the reassurance caption past this point. */
export const SLOW_TURN_MS = 4000;

/** A turn that has not resolved by here becomes a retryable error bubble.
 *  35s clears the backend's own 30-second retrieval ceiling with margin: at
 *  15s this timer raced turns the pipeline legitimately finishes — and
 *  commits — in 20–30s, so the bubble was discarding real replies (store
 *  tracker 6.29). */
export const TURN_TIMEOUT_MS = 35000;

/** Minimum "thinking" beat so an instant reply doesn't flash. */
export const MIN_THINKING_MS = 1000;

// `Question` is not exported by the api module; borrow it from `ask` itself.
// Type-only imports: nothing from the api module is loaded at runtime.
type Question = Parameters<typeof api.question.ask>[0];

type TimerHandle = ReturnType<typeof setTimeout>;

/**
 * The render values and effect callbacks a main turn needs. FormsContext
 * assigns a fresh object into `depsRef.current` in its render body (phase 2 of
 * the hook, design D4), once its last dependency is defined. Plain function
 * properties only: the object is shallow-copied at the call.
 */
export type FormsTurnDeps = {
    /* Live state */
    /** Reads the latest forms state when called (`stateRef.current`). */
    getFormsState: () => CfState;

    /* Render values */
    /** Chat history; the module drops temp bubbles and keeps the last four. */
    messages: ChatMessage[];
    sessionid: string | null;
    pageUrl: string | null | undefined;
    indexName: string | null;
    /** `config.CustomPrompt`; the module applies `?? false`. */
    customPrompt: boolean | undefined;
    registry: CfRegistry | null;
    /** The form a turn belongs to for a given state (active, else primary). */
    formFor: (state: CfState) => CfForm | null;

    /* Effects */
    /** Forms dispatch (`removeQueued`). */
    dispatch: (action: CfAction) => void;
    addMessage: (message: ChatMessage) => void;
    removeMessage: (index: number) => void;
    reconcileTurn: (turnId: string, message: ChatMessage) => void;
    commitFills: (ownerFormId: string | undefined, fills: FormFill[] | undefined) => FillReceipt[] | undefined;
    activate: (formId: string, options: { activationTurn: boolean }) => void;
    routeOffer: (offeredId: string | undefined) => string | undefined;
    routeQueued: (queuedId: string | undefined) => void;
    applyAskSignal: (result: Answer, ownerFormId: string | null | undefined, honorActivation?: boolean) => void;
    setThinking: (thinking: boolean) => void;
    setSlowTurn: (slow: boolean) => void;
};

/** Phase 1 of the hook creates this empty; phase 2 fills it every render. */
export type FormsTurnDepsRef = { current: FormsTurnDeps | null };

export type FormsTurnConfig = {
    /** The backend call (`api.question.ask`). */
    ask: (body: Question, customPrompt: boolean) => Promise<Answer>;
    now: () => number;
    sleep: (ms: number) => Promise<unknown>;
    setTimeout: (fn: () => void, ms: number) => TimerHandle;
    clearTimeout: (handle: TimerHandle | undefined) => void;
    /** A fresh id for a timed-out turn (`uuidv4`). */
    newTurnId: () => string;
    depsRef: FormsTurnDepsRef;
};

export type TransportRequest = {
    /**
     * The request body, or a thunk building it. A thunk runs where the body
     * was built before this module existed (inside the try, after the slow
     * timer is armed), so a body builder that throws still resolves to a
     * failed turn rather than escaping the runner.
     */
    body: Question | (() => Question);
    /** Second argument of `ask` (`config.CustomPrompt ?? false`). */
    customPrompt: boolean;
};

export type TransportResult = {
    /** The settled answer, or null on transport failure or timeout. */
    result: Answer | null;
    /** `classifyTurn(result)`; `retryable` whenever `result` is null. */
    outcome: TurnOutcome;
    /**
     * True when the turn timer fired, whether or not the race had already
     * settled with an answer (both can land in one timer advance). Returned
     * raw: callers decide on `timedOut && askPromise`, as before.
     */
    timedOut: boolean;
    /**
     * The raw ask promise, null if `ask` threw synchronously. This module
     * attaches no handlers to it: each caller attaches its own late handler
     * (design D1 item 14).
     */
    askPromise: Promise<Answer> | null;
};

export type FormsTurn = {
    /**
     * Queue a visitor turn for `text`. Copies the render values synchronously
     * at the call. Resolves when that turn's runner finishes (reply applied,
     * or failure surface added); never waits for a late reply; never rejects.
     */
    sendVisitorTurn: (text: string) => Promise<void>;
    /**
     * Remove the message at `messageIndex`, then queue `text` as a visitor
     * turn. The render values are copied before the removal, so the turn's
     * history still contains the removed message. Same return semantics as
     * `sendVisitorTurn`.
     */
    retryVisitorTurn: (messageIndex: number, text: string) => Promise<void>;
    /**
     * Queue the hidden activation turn for `formId`. Reads the render values
     * when the queue starts it. `fallbackText` is the acknowledgement shown
     * when the turn fails. Same return semantics as `sendVisitorTurn`.
     */
    runActivationTurn: (formId: string, fallbackText: string) => Promise<void>;
    /**
     * Send one request and resolve once the turn is settled and the minimum
     * thinking beat has elapsed. Never queued. `onSlow(true)` fires after
     * `SLOW_TURN_MS`; `onSlow(false)` fires unconditionally right after the
     * race settles, before the beat (design D1 item 13).
     */
    runTransport: (request: TransportRequest, onSlow: (isSlow: boolean) => void) => Promise<TransportResult>;
};

type Kind = 'visitor' | 'activation';
type Delivery = 'fresh' | 'late';

type MainTurn =
    | { kind: 'visitor'; text: string }
    | { kind: 'activation'; formId: string; fallbackText: string };

export const createFormsTurn = (config: FormsTurnConfig): FormsTurn => {
    const { ask, now, sleep, setTimeout: setTimer, clearTimeout: clearTimer, newTurnId, depsRef } = config;

    /* ---------------------------------------------------------------
       Transport core (design D2 part 1)
    ----------------------------------------------------------------*/
    const runTransport = async (
        { body, customPrompt }: TransportRequest,
        onSlow: (isSlow: boolean) => void
    ): Promise<TransportResult> => {
        const slowTimer = setTimer(() => onSlow(true), SLOW_TURN_MS);
        const startedAt = now();

        let result: Answer | null = null;
        // Held outside the race so a timed-out turn keeps a handle on the
        // still-running request; the caller reconciles a late reply.
        let askPromise: Promise<Answer> | null = null;
        let timedOut = false;
        let timeoutTimer: TimerHandle | undefined;
        try {
            askPromise = ask(typeof body === 'function' ? body() : body, customPrompt);
            result = await Promise.race([
                askPromise,
                new Promise<never>((_, reject) => {
                    timeoutTimer = setTimer(() => {
                        timedOut = true;
                        reject(new Error('timeout'));
                    }, TURN_TIMEOUT_MS);
                }),
            ]);
        } catch (_) {
            result = null;
        }

        clearTimer(timeoutTimer);
        clearTimer(slowTimer);
        onSlow(false);

        const elapsed = now() - startedAt;
        if (elapsed < MIN_THINKING_MS) await sleep(MIN_THINKING_MS - elapsed);

        const outcome = classifyTurn(result);
        return { result, outcome, timedOut, askPromise };
    };

    /* ---------------------------------------------------------------
       Turn queue and the live-turn set (design D2 part 2)
    ----------------------------------------------------------------*/
    // Timed-out turns whose request is still running and whose error bubble is
    // still live (store tracker 6.29). Membership is the "bubble still live"
    // test on this side: any newer main turn — a Try again, a fresh message, a
    // Start or a Switch — clears the whole set when it is queued, because the
    // conversation has moved past every bubble that was waiting. The
    // reducer's id lookup covers the bubble's own removal.
    const liveTurnIds = new Set<string>();

    let chain: Promise<void> = Promise.resolve();

    /**
     * FIFO. Ends liveness synchronously, then starts `run` in a `.then`
     * continuation of the previous turn — never synchronously, even on an idle
     * queue (design D1 item 15; do not add an `if (!busy) run()` fast path).
     * A throwing `run` never stalls the queue and the returned promise never
     * rejects.
     */
    const enqueue = (run: () => Promise<void>): Promise<void> => {
        liveTurnIds.clear();
        const next = chain.then(run).catch(() => undefined);
        chain = next;
        return next;
    };

    /* ---------------------------------------------------------------
       Main-turn reply applier (design D2 part 3)
    ----------------------------------------------------------------*/
    type ApplyArgs = {
        deps: FormsTurnDeps;
        kind: Kind;
        delivery: Delivery;
        result: Answer;
        outcome: TurnOutcome;
        /** The owner form, captured when the turn was sent. */
        ownerFormId: string | undefined;
        /** Visitor text (retry text for `fillRetryText`); '' for activation. */
        text: string;
        /** Late only: the timed-out turn's id. */
        turnId?: string;
        /** Late only: whether the turn was still live when the reply landed. */
        live?: boolean;
    };

    /**
     * The one place a main turn's reply takes effect. The order of effects is
     * load-bearing: the ask signal comes after `form_activated`,
     * `form_declined` and `form_queued` (chips-follow-ask), and the sequences
     * for fresh/late and visitor/activation are the six in design D2.
     */
    const applyReply = ({ deps, kind, delivery, result, outcome, ownerFormId, text, turnId, live }: ApplyArgs): void => {
        const late = delivery === 'late';

        // Fills commit whether or not the turn is live: the backend already
        // persisted them at extraction, and the reducer's auto-mode guard
        // (6.21) keeps a late fill from overwriting a field a newer turn
        // confirmed. F3: bound to the owner captured when the turn was sent.
        const receipts = deps.commitFills(ownerFormId, result.fills);

        // Engagement closure (store tracker 6.55): pin only from the
        // response's activation signal (an in-window offer acceptance), never
        // from the fill alone. A fresh activation reply ignores the signal;
        // a late reply of either kind applies it (the re-pin is guarded by
        // `activate` itself, and is a no-op for the turn's own form).
        if (
            (kind === 'visitor' || late) &&
            result.form_activated &&
            deps.registry?.byId.get(result.form_activated) &&
            deps.getFormsState().activeFormId !== result.form_activated
        ) {
            deps.activate(result.form_activated, { activationTurn: false });
        }
        // A declined offer (a negative reply inside the offer window) is
        // quiet: the queue entry, if any, is removed; no data is touched.
        if (result.form_declined) deps.dispatch({ type: 'removeQueued', formId: result.form_declined });
        deps.routeQueued(result.form_queued ?? undefined);

        if (late) {
            // A superseded late reply never sets the ask, routes an offer or
            // renders.
            if (!live) return;
            // Store ledger 6.108 (D2): the timed-out activation turn's late
            // reply never signals an ask (its answer is never rendered).
            if (kind === 'visitor') deps.applyAskSignal(result, ownerFormId);
        } else if (kind === 'visitor') {
            // Store ledger 6.108 (D2): after activation handling above, so a
            // verbal-switch reply ends with the new form's ask set.
            deps.applyAskSignal(result, ownerFormId);
        } else {
            // The activation reply is the new form's own first ask.
            deps.applyAskSignal(result, ownerFormId, false);
        }
        const cardOfferId = deps.routeOffer(result.form_offer ?? undefined);

        const message: ChatMessage = {
            content: result.answer,
            direction: 1,
            time: new Date(now()),
            relatedLinks: result.links,
            cards: result.cards,
            fills: receipts?.length ? receipts : undefined,
            formOfferId: cardOfferId,
            formCompleteId: result.form_complete ?? undefined,
            // 1.1 / bug cluster D: the answer always renders — this never
            // masks it — but a retryable extraction error still earns its own
            // quiet retry affordance. Visitor turns only: an activation turn
            // has no visitor-typed text to retry.
            ...(kind === 'visitor' ? { fillRetryText: fillsRetryable(result) ? text : undefined } : {}),
            // The reply stands; the notice says the fill did not land, so a
            // student who volunteered details is never left assuming they
            // were recorded. When the turn carried fills too, receipts render
            // beside the notice and the copy narrows to "some" (platform
            // tracker 6.17).
            fillNotice: outcome === 'unrecorded' ? (receipts?.length ? 'partial' : true) : undefined,
        };

        if (late) {
            // Replaces the timed-out bubble in place, if it still exists.
            deps.reconcileTurn(turnId as string, message);
        } else {
            deps.addMessage(message);
            deps.setThinking(false);
        }
    };

    /**
     * A timed-out turn's response arrived after all (store tracker 6.29): the
     * timer used to race a pipeline that legitimately commits in 20–30s, and
     * the real reply — answer, receipts, offer — was discarded on the floor.
     * Runs detached from the FIFO queue, which released at timeout; nothing
     * here re-blocks or reorders it. A late *failure* never reaches here — the
     * bubble already offers Try again, so it changes nothing.
     *
     * Liveness is consumed first. A retryable late reply is dropped entirely,
     * whether or not its turn is live (design D1 item 5).
     */
    const onLateReply = (
        deps: FormsTurnDeps,
        kind: Kind,
        turnId: string,
        text: string,
        ownerFormId: string | undefined,
        result: Answer
    ): void => {
        const live = liveTurnIds.delete(turnId);
        const outcome = classifyTurn(result);
        if (outcome === 'retryable') return;
        applyReply({ deps, kind, delivery: 'late', result, outcome, ownerFormId, text, turnId, live });
    };

    /** A late failure changes nothing but liveness: the bubble already says try again. */
    const onLateRejection = (turnId: string): void => {
        liveTurnIds.delete(turnId);
    };

    /* ---------------------------------------------------------------
       Main-turn runner
    ----------------------------------------------------------------*/
    const runMainTurn = async (turn: MainTurn, deps: FormsTurnDeps): Promise<void> => {
        const { registry } = deps;
        if (!registry) return;
        const kind = turn.kind;
        const time = () => new Date(now());

        // Start (design D1 item 12). Typing indicator: an empty temp bot
        // message is what the existing Message component renders as
        // MessageSending.
        deps.addMessage({ content: '', direction: 1, time: time(), temp: true });
        deps.setThinking(true);

        // Forms state is read live, here, when the turn leaves the queue.
        const state = deps.getFormsState();
        // The owner form, captured before the turn's own await. The active
        // form drives a visitor turn (multiform rule): its open fields and
        // only its — never another form's — plus the config form before
        // anything starts. An activation turn's owner is its own form.
        const ownerForm = turn.kind === 'visitor' ? deps.formFor(state) : registry.byId.get(turn.formId) ?? null;
        const ownerFormId = turn.kind === 'visitor' ? ownerForm?.objectId : turn.formId;
        const text = turn.kind === 'visitor' ? turn.text : '';
        const history = deps.messages.filter((m) => !m.temp).slice(-4);

        const buildBody = (): Question =>
            turn.kind === 'visitor'
                ? {
                      question: text,
                      index: deps.indexName,
                      context: history,
                      sessionid: deps.sessionid,
                      current_url: deps.pageUrl || undefined,
                      // Hidden conditional fields stay out (registry-wide
                      // chain resolution, tracker 6.26b): a child revealed by
                      // this turn's own fills enters the list on the NEXT
                      // turn, mirroring the backend's per-turn resolution.
                      open_fields: toOpenFieldSpecs(ownerForm ? openForAI(ownerForm, state, registry) : []),
                      // Store tracker 6.64: the union of fields the visitor
                      // has explicitly confirmed in the form view (or via a
                      // suggestion confirm), so extraction never re-fills a
                      // field the visitor has locked in. This only runs while
                      // `registry` is non-null (guarded above), so a
                      // legacy/no-forms turn never carries this field at all.
                      confirmed_field_ids: state.confirmedFieldIds,
                      // Platform contract (tracker 6.15): `form_objectid`
                      // names a form the student is *actively filling*, and
                      // the offer selector reads it as "do not offer this
                      // one". Sending it before anything is started told the
                      // server to exclude the only form this tenant has, so
                      // the form was never offered and the start card could
                      // not appear. Open fields still ride every turn — this
                      // marker is about what is in progress, not what may be
                      // extracted.
                      form_objectid: state.activeFormId ?? undefined,
                  }
                : {
                      question: '',
                      index: deps.indexName,
                      context: history,
                      sessionid: deps.sessionid,
                      current_url: deps.pageUrl || undefined,
                      open_fields: toOpenFieldSpecs(ownerForm ? openForAI(ownerForm, state, registry) : []),
                      form_objectid: turn.formId,
                      activate_form: turn.formId,
                      // Store tracker 6.64: sent on every forms-active turn,
                      // this hidden activation turn included.
                      confirmed_field_ids: state.confirmedFieldIds,
                  };

        // Late handle: `runTransport` hands back the still-running request —
        // that is what late reconciliation observes (store tracker 6.29).
        const { result, outcome, timedOut, askPromise } = await runTransport(
            { body: buildBody, customPrompt: deps.customPrompt ?? false },
            (isSlow) => deps.setSlowTurn(isSlow)
        );

        // A turn resolves to one of three surfaces; see `classifyTurn`.
        if (!result || outcome === 'retryable') {
            if (turn.kind === 'visitor') {
                // The user's message stays; only this turn resolves to the
                // bubble. The two retryable surfaces make different claims
                // (store tracker 6.29): a failed call or server-declared
                // retryable error may promise nothing was lost — that rides
                // the platform guarantee — but at a client timeout the backend
                // may still commit the turn, so that bubble promises nothing,
                // carries the turn's identity, and leaves the request running
                // for a late success to reconcile into it. Returning releases
                // the FIFO queue either way.
                const turnId = timedOut && askPromise ? newTurnId() : undefined;
                deps.addMessage({
                    content: turnId ? TURN_TIMEOUT_MESSAGE : TURN_ERROR_MESSAGE,
                    direction: 1,
                    time: time(),
                    formError: true,
                    retryText: text,
                    turnId,
                });
                deps.setThinking(false);
                if (turnId && askPromise) {
                    liveTurnIds.add(turnId);
                    askPromise.then(
                        (late) => onLateReply(deps, kind, turnId, text, ownerFormId, late),
                        () => onLateRejection(turnId)
                    );
                }
                return;
            }

            // Degrade gracefully: no error card, no retry (there is no
            // visitor-typed text to retry) — the local acknowledgement still
            // narrates the transition so the moment never reads as a dead
            // end. `unaskedAck` keeps it out of the quick-reply chips'
            // eligibility (it asks no field, unlike a real reply) — covers
            // both branches: an empty/retryable answer and a transport
            // failure or timeout alike.
            deps.addMessage({ content: turn.fallbackText, direction: 1, time: time(), unaskedAck: true });
            deps.setThinking(false);
            // F8: a client timeout does not mean the backend gave up —
            // reconcile a late response through the same applier, bound to
            // THIS form (the activation turn always has exactly one,
            // unambiguous owner). The fallback carries no turn id, so a late
            // reply has no bubble to land in.
            if (timedOut && askPromise) {
                const turnId = newTurnId();
                liveTurnIds.add(turnId);
                askPromise.then(
                    (late) => onLateReply(deps, kind, turnId, '', ownerFormId, late),
                    () => onLateRejection(turnId)
                );
            }
            return;
        }

        applyReply({ deps, kind, delivery: 'fresh', result, outcome, ownerFormId, text });
    };

    /* ---------------------------------------------------------------
       Entry points (design D3)
    ----------------------------------------------------------------*/
    // The null-deps guards below are unreachable in the provider: phase 2
    // assigns `depsRef.current` on every render, before any handler can run.
    // They still go through `enqueue`, so liveness clears and FIFO holds, and
    // the queued run is then a no-op that resolves.
    const sendVisitorTurn = (text: string): Promise<void> => {
        const current = depsRef.current;
        // Snapshot at the call (D1 item 11): the queued run and its late
        // handler use this copy, not whatever render comes later.
        const deps = current ? { ...current } : null;
        return enqueue(() => (deps ? runMainTurn({ kind: 'visitor', text }, deps) : Promise.resolve()));
    };

    const retryVisitorTurn = (messageIndex: number, text: string): Promise<void> => {
        const current = depsRef.current;
        // Copied before the removal: a retry's history still contains the
        // message being removed.
        const deps = current ? { ...current } : null;
        // Unreachable null case: nothing to remove it with, so the removal is
        // skipped and the queued run is a no-op.
        deps?.removeMessage(messageIndex);
        return enqueue(() => (deps ? runMainTurn({ kind: 'visitor', text }, deps) : Promise.resolve()));
    };

    /**
     * The hidden activation turn (2.2, store tracker 6.55): a client-sent,
     * visitor-invisible turn through the standard `/question/` pipeline
     * carrying `activate_form`/`form_objectid` for `formId` and an empty
     * question. The response is one server-composed message that both
     * acknowledges the activation and asks the active form's next field (or
     * carries the completion signal when it was already complete ambiently)
     * — this replaces the client-posted "Switched to…"/start acknowledgement.
     *
     * `fallbackText` is the graceful degrade (task 2.2's "if the backend
     * doesn't support it… degrade gracefully rather than leaving a dead
     * end"): a legacy backend that does not yet recognize `activate_form`
     * answers with nothing usable, and rather than leave the typing
     * indicator a dead end or show a scary retry card for a message the
     * student never typed, the old local acknowledgement text stands in.
     * The caller (`activate`/`switchToForm`) has already pinned the form
     * synchronously before this runs — this only produces the chat message.
     *
     * Reads the render values when the queue starts it (latest render, D1
     * item 11). The runner's promise is returned directly, so the queued link
     * settles exactly when the runner does. The null case is unreachable (see
     * above).
     */
    const runActivationTurn = (formId: string, fallbackText: string): Promise<void> =>
        enqueue(() => {
            const current = depsRef.current;
            return current
                ? runMainTurn({ kind: 'activation', formId, fallbackText }, { ...current })
                : Promise.resolve();
        });

    return { sendVisitorTurn, retryVisitorTurn, runActivationTurn, runTransport };
};
